import { AfterViewInit, Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { forkJoin, map, Observable, of, switchMap, tap } from 'rxjs';

import { AlertDeliveryChannel, AlertDeliveryStatus, AlertEventState, AlertSeverityLevel } from 'src/app/enums/alert.enum';
import { AlertDeliveryLog, AppNotification } from 'src/app/models/notification';
import { BaseGridComponent, Filter } from 'src/app/shared/grid/baseGrid.component';
import { CommonService } from 'src/app/services/common.service';
import { MatDialog } from '@angular/material/dialog';
import { NotificationService } from 'src/app/services/notification.service';
import { ToastService } from 'src/app/services/toast.service';
import { getEnumLookup } from 'src/app/_helpers/enum-helper';
import { GridViewService } from 'src/app/services/grid-view.service';
import { detailExpandAnimation } from 'src/app/animations/detailExpand.animation';
import { trackByIndex } from 'src/app/shared/utils/track-by.util';
import { OrganizationLevelService } from 'src/app/services/organizationLevel.service';
import { ParameterService } from 'src/app/services/parameter.service';
import { Lookup } from 'src/app/models/lookup.model';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { UserService } from 'src/app/services/user.service';
import { DateFormatService } from 'src/app/services/dateFormat.service';
import { ActivatedRoute, Router } from '@angular/router';
import { DateFilterTypeEnum } from 'src/app/enums/dateFilterType.enum';
import { toTimestampMs } from 'src/app/shared/utils/timestamp.util';
import { buildAlertGraphWindowMs } from 'src/app/shared/utils/alert-graph-window.util';

@Component({
  standalone: false,
  selector: 'app-notification-list',
  templateUrl: './notification-list.component.html',
  styleUrls: ['./notification-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  animations: [detailExpandAnimation],
})
export class NotificationListComponent extends BaseGridComponent<AppNotification> implements OnInit, AfterViewInit {

  readonly alertEventStateEnum = AlertEventState;

  readonly severityLookup = getEnumLookup(AlertSeverityLevel);
  readonly stateLookup = getEnumLookup(AlertEventState);
  readonly stateBadgeClassMap: Record<AlertEventState, string> = {
    [AlertEventState.Active]: 'state-active',
    [AlertEventState.Acknowledged]: 'state-acknowledged',
    [AlertEventState.Resolved]: 'state-resolved',
    [AlertEventState.Escalated]: 'state-escalated',
  };
  private readonly stateLabelMap: Record<AlertEventState, string> = {
    [AlertEventState.Active]: 'Active',
    [AlertEventState.Resolved]: 'Resolved',
    [AlertEventState.Acknowledged]: 'Acknowledged',
    [AlertEventState.Escalated]: 'Escalated',
  };
  private readonly deliveryStatusLabelMap: Record<AlertDeliveryStatus, string> = {
    [AlertDeliveryStatus.Pending]: 'Pending',
    [AlertDeliveryStatus.Sent]: 'Sent',
    [AlertDeliveryStatus.Delivered]: 'Delivered',
    [AlertDeliveryStatus.Failed]: 'Failed',
    [AlertDeliveryStatus.Opened]: 'Opened',
  };
  private readonly deliveryStatusLabelByName: Record<string, string> = {
    pending: 'Pending',
    sent: 'Sent',
    delivered: 'Delivered',
    failed: 'Failed',
    opened: 'Opened',
    notdelivered: 'Not Delivered',
  };
  private readonly deliveryChannelLabelMap: Record<AlertDeliveryChannel, string> = {
    [AlertDeliveryChannel.Email]: 'Email',
    [AlertDeliveryChannel.SMS]: 'SMS',
    [AlertDeliveryChannel.InApp]: 'In App',
    [AlertDeliveryChannel.Push]: 'Push',
  };

  selectedSeverities: number[] = [];
  selectedStates: number[] = [
    AlertEventState.Active,
    AlertEventState.Acknowledged,
    AlertEventState.Resolved,
    AlertEventState.Escalated,
  ];
  selectedSiteId: string | null = null;
  selectedParameterId: string | null = null;
  startDate = '';
  endDate = '';
  isAlertFilterPanelOpen = false;
  readonly trackByIndex = trackByIndex;
  siteOptions: Lookup[] = [];
  parameterOptions: Lookup[] = [];
  private readonly recipientNameCache = new Map<string, string>();
  private pendingAlertEventId: string | null = null;
  readonly dateTimeFormat: string;

  constructor(
    protected notificationService: NotificationService,
    toastService: ToastService,
    commonService: CommonService,
    dialog: MatDialog,
    protected gridViewService: GridViewService,
    private organizationLevelService: OrganizationLevelService,
    private parameterService: ParameterService,
    private userService: UserService,
    private dateFormatService: DateFormatService,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    super(toastService, dialog, commonService, gridViewService);
    this.dateTimeFormat = this.dateFormatService.userDateWithTimeFormat;
    this.gridService = notificationService;
    this.displayedColumns = [
      'expand',
      nameOf<AppNotification>(x => x.notificationSentDate),
      nameOf<AppNotification>(x => x.notificationName),
      nameOf<AppNotification>(x => x.severity),
      nameOf<AppNotification>(x => x.state),
      'endPoint.Name',
      'Parameter.AnalyteName',
      'action',
      'graphAction',
    ];
    this.defaultOrderBy = 'desc';
  }

  override get hasOrgFilter(): boolean {
    return true;
  }

  ngOnInit(): void {
    this.initializeDefaultDateRange();
    super.ngOnInit();
    this.subscriptions.push(
      this.route.queryParamMap.subscribe((params) => {
        const alertEventId = params.get('alertEventId');
        this.pendingAlertEventId = alertEventId || null;
        this.tryOpenAlertFromQuery(this.dataSource.getData());
      })
    );
    this.subscriptions.push(
      this.dataSource.entitySubject.subscribe((notifications) => {
        this.applyDisplayFields(notifications, false);
        this.tryOpenAlertFromQuery(notifications);
      })
    );
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.loadHeaderData();
  }

  public override loadHeaderData(): void {
    super.loadHeaderData();

    if (this.siteOptions.length && this.parameterOptions.length) {
      return;
    }

    this.subscriptions.push(
      forkJoin([
        this.organizationLevelService.getAll(''),
        this.parameterService.getAll()
      ]).subscribe({
        next: ([orgs, params]) => {
          this.siteOptions = orgs || [];
          this.parameterOptions = params || [];
          this.applyDisplayFields(this.dataSource.getData(), true);
        },
        error: (error) => {
          this.toastService.showError(error, 'Failed to load dropdown data');
        }
      })
    );
  }

  private initializeDefaultDateRange(): void {
    const end = new Date();
    const start = new Date(end.getTime() - (30 * 24 * 60 * 60 * 1000));
    this.startDate = this.toDateInputValue(start);
    this.endDate = this.toDateInputValue(end);
  }

  private toDateInputValue(value: Date): string {
    return this.dateFormatService.formatDateTimeToString(
      value,
      this.dateFormatService.canonicalDateFormat,
    );
  }

  toggleAlertFilterPanel(): void {
    this.isAlertFilterPanelOpen = !this.isAlertFilterPanelOpen;
  }

  closeAlertFilterPanel(): void {
    this.isAlertFilterPanelOpen = false;
  }

  private applyDisplayFields(notifications: AppNotification[], refreshFromLookupData: boolean): void {
    if (!notifications?.length) {
      return;
    }

    notifications.forEach((notification) => this.applyDisplayField(notification, refreshFromLookupData));

    if (refreshFromLookupData) {
      this.dataSource.updateData([...notifications]);
    }
  }

  private applyDisplayField(notification: AppNotification, refreshFromLookupData: boolean): void {
    const effectiveState = this.resolveAlertState(notification.state);
    notification.effectiveState = effectiveState;
    notification.stateLabel = this.stateLabelMap[effectiveState] || this.stateLabelMap[AlertEventState.Active];
    notification.stateBadgeClass = this.stateBadgeClassMap[effectiveState] || this.stateBadgeClassMap[AlertEventState.Active];
    notification.alertDate = notification.startedAt || notification.sentDate || notification.notificationSentDate || '';
    notification.isExpanded = !!notification.isExpanded;

    if (refreshFromLookupData || !notification.siteDisplayName || notification.siteDisplayName === 'N/A') {
      const site = this.siteOptions.find((item) => item.id === notification.siteId);
      notification.siteDisplayName = notification.siteName || site?.name || notification.endPoint || 'N/A';
    }

    if (refreshFromLookupData || !notification.parameterDisplayName || notification.parameterDisplayName === 'N/A') {
      const parameter = this.parameterOptions.find((item) => item.id === notification.parameterId);
      notification.parameterDisplayName = notification.parameterName || parameter?.name || 'N/A';
    }

    (notification.deliveryLogs || []).forEach((delivery) => {
      delivery.recipientDisplayName =
        delivery.recipientName ||
        delivery.recipientEmail ||
        (delivery.userId ? this.recipientNameCache.get(delivery.userId) : undefined) ||
        delivery.userId ||
        'N/A';
      delivery.displayStatusLabel = this.resolveDeliveryStatusLabel(delivery.status);
      delivery.displayChannelLabel = this.resolveDeliveryChannelLabel(delivery.channel);
      delivery.displayDate = delivery.sentAt || delivery.attemptedAt || delivery.deliveredAt || delivery.openedAt;
    });

    (notification.stateTransitions || []).forEach((transition) => {
      transition.displayToStateLabel = this.resolveStateLabel(transition.toState);
    });

    const acknowledgedTransition = (notification.stateTransitions || []).find((transition) =>
      this.resolveAlertState(transition.toState as AlertEventState | string) === AlertEventState.Acknowledged);

    if (!notification.acknowledgedAt && acknowledgedTransition?.transitionedAt) {
      notification.acknowledgedAt = acknowledgedTransition.transitionedAt;
    }

    notification.stateTransitionsLoaded = notification.stateTransitionsLoaded || !!notification.stateTransitions?.length;
    notification.deliveryLogsLoaded = notification.deliveryLogsLoaded || !!notification.deliveryLogs?.length;
    notification.deliveryLogsLoadError ??= false;
  }

  private tryOpenAlertFromQuery(notifications: AppNotification[]): void {
    if (!notifications?.length || this.pendingAlertEventId == null) {
      return;
    }

    const matchedNotification = notifications.find((notification) => notification.id === this.pendingAlertEventId);
    if (!matchedNotification) {
      return;
    }

    if (!matchedNotification.isExpanded) {
      this.toggleAlertRow(matchedNotification);
    }

    this.pendingAlertEventId = null;
  }

  private resolveAlertState(state: AlertEventState | string | undefined): AlertEventState {
    if (typeof state === 'number') {
      return state;
    }

    if (typeof state === 'string') {
      const normalizedState = state.toLowerCase();
      if (normalizedState === 'resolved') {
        return AlertEventState.Resolved;
      }
      if (normalizedState === 'acknowledged') {
        return AlertEventState.Acknowledged;
      }
      if (normalizedState === 'escalated') {
        return AlertEventState.Escalated;
      }
    }

    return AlertEventState.Active;
  }

  private resolveDeliveryStatusLabel(status: number | string | undefined): string {
    if (typeof status === 'number') {
      return this.deliveryStatusLabelMap[status as AlertDeliveryStatus] || 'N/A';
    }

    if (typeof status !== 'string') {
      return '';
    }

    const normalizedStatusKey = status.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
    const knownStatusLabel = this.deliveryStatusLabelByName[normalizedStatusKey];
    if (knownStatusLabel) {
      return knownStatusLabel;
    }

    const readableStatus = status
      .replace(/_/g, ' ')
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .trim();

    return readableStatus || 'N/A';
  }

  private resolveDeliveryChannelLabel(channel: number | string | undefined): string {
    if (typeof channel === 'number') {
      return this.deliveryChannelLabelMap[channel as AlertDeliveryChannel] || String(channel);
    }

    return channel || '';
  }

  private resolveStateLabel(state: number | string | undefined): string {
    if (typeof state === 'number') {
      return this.stateLabelMap[state as AlertEventState] || String(state);
    }

    if (typeof state === 'string') {
      return state;
    }

    return this.stateLabelMap[AlertEventState.Active];
  }

  toggleAlertRow(notification: AppNotification): void {
    notification.isExpanded = !notification.isExpanded;

    if (notification.isExpanded && !notification.deliveryLogsLoaded) {
      this.loadDeliveryLogs(notification);
    }

    if (notification.isExpanded && !notification.stateTransitionsLoaded) {
      this.loadStateTransitions(notification);
    }
  }

  private loadDeliveryLogs(notification: AppNotification): void {
    if (!notification.id) {
      return;
    }

    notification.isLoadingDeliveryLogs = true;
    notification.deliveryLogsLoadError = false;

    this.subscriptions.push(
      this.notificationService.getDeliveryLogs(notification.id).pipe(
        switchMap((deliveryLogs) => this.populateRecipientDisplayNames(deliveryLogs)),
      ).subscribe({
        next: (deliveryLogs) => {
          deliveryLogs.forEach((delivery) => {
            delivery.displayStatusLabel = this.resolveDeliveryStatusLabel(delivery.status);
            delivery.displayChannelLabel = this.resolveDeliveryChannelLabel(delivery.channel);
            delivery.displayDate = delivery.sentAt || delivery.attemptedAt || delivery.deliveredAt || delivery.openedAt;
          });
          notification.deliveryLogs = deliveryLogs;
          notification.deliveryLogsLoaded = true;
          notification.deliveryLogsLoadError = false;
          notification.isLoadingDeliveryLogs = false;
        },
        error: (error) => {
          notification.deliveryLogs = [];
          notification.deliveryLogsLoaded = false;
          notification.deliveryLogsLoadError = true;
          notification.isLoadingDeliveryLogs = false;
          this.toastService.showError(error, 'Failed to load delivery logs');
        },
      })
    );
  }

  private populateRecipientDisplayNames(deliveryLogs: AlertDeliveryLog[]): Observable<AlertDeliveryLog[]> {
    const unresolvedUserIds = [...new Set(
      deliveryLogs
        .map((delivery) => delivery.userId)
        .filter((userId): userId is string =>
          !!userId &&
          !this.recipientNameCache.has(userId) &&
          !deliveryLogs.some((delivery) =>
            delivery.userId === userId &&
            (!!delivery.recipientName || !!delivery.recipientEmail),
          ),
        ),
    )];

    if (!unresolvedUserIds.length) {
      return of(this.applyRecipientDisplayNames(deliveryLogs));
    }

    return forkJoin(
      unresolvedUserIds.map((userId) => this.userService.getUserName(userId))
    ).pipe(
      tap((resolvedNames) => {
        unresolvedUserIds.forEach((userId, index) => {
          const resolvedName = resolvedNames[index];
          if (resolvedName && resolvedName !== 'N/A') {
            this.recipientNameCache.set(userId, resolvedName);
          }
        });
      }),
      map(() => this.applyRecipientDisplayNames(deliveryLogs))
    );
  }

  private applyRecipientDisplayNames(deliveryLogs: AlertDeliveryLog[]): AlertDeliveryLog[] {
    return deliveryLogs.map((delivery) => ({
      ...delivery,
      recipientDisplayName:
        delivery.recipientName ||
        delivery.recipientEmail ||
        (delivery.userId ? this.recipientNameCache.get(delivery.userId) : undefined) ||
        delivery.userId ||
        'N/A',
    }));
  }

  private loadStateTransitions(notification: AppNotification): void {
    if (!notification.id) {
      return;
    }

    notification.isLoadingStateTransitions = true;

    this.subscriptions.push(
      this.notificationService.getAlertEventTransitions(notification.id).subscribe({
        next: (transitions) => {
          transitions.forEach((transition) => {
            transition.displayToStateLabel = this.resolveStateLabel(transition.toState);
          });
          notification.stateTransitions = transitions;
          notification.stateTransitionsLoaded = true;
          notification.isLoadingStateTransitions = false;
        }
      })
    );
  }

  showSuppressionReason(notification: AppNotification): void {
    const suppressionReason = notification.suppressionReason?.trim();
    this.toastService.showInfo(suppressionReason || 'Suppressed', !suppressionReason);
  }

  applyAlertFilters(): void {
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
    this.loadPage();
    this.closeAlertFilterPanel();
  }

  resetAlertFilters(): void {
    this.selectedSeverities = [];
    this.selectedStates = [
      AlertEventState.Active,
      AlertEventState.Acknowledged,
      AlertEventState.Resolved,
      AlertEventState.Escalated,
    ];
    this.selectedSiteId = null;
    this.selectedParameterId = null;
    this.initializeDefaultDateRange();
    this.applyAlertFilters();
  }

  override getFilterBy(): Filter {
    const filterBy = super.getFilterBy();

    if (this.selectedSeverities.length > 0) {
      filterBy.severity = this.selectedSeverities.join(',');
    }

    if (this.selectedStates.length > 0) {
      filterBy.state = this.selectedStates.join(',');
    }

    if (this.selectedSiteId != null) {
      filterBy.siteId = this.selectedSiteId;
      filterBy.organizationLevelId = this.selectedSiteId;
    }

    if (this.selectedParameterId != null) {
      filterBy.parameterId = this.selectedParameterId;
    }

    if (this.startDate) {
      filterBy.startDate = new Date(`${this.startDate}T00:00:00`).toISOString();
    }

    if (this.endDate) {
      filterBy.endDate = new Date(`${this.endDate}T23:59:59`).toISOString();
    }

    return filterBy;
  }

  acknowledgeAlert(notification: AppNotification): void {
    if (!notification.id || notification.isUpdatingState) {
      return;
    }

    notification.isUpdatingState = true;

    this.subscriptions.push(
      this.notificationService.acknowledgeAlertEvent(notification.id).subscribe({
        next: () => {
          notification.isUpdatingState = false;
          this.toastService.showSuccess('Alert acknowledged successfully', true);
          this.loadPage();
        },
        error: (error) => {
          notification.isUpdatingState = false;
          this.toastService.showError(error);
        }
      })
    );
  }

  resolveAlert(notification: AppNotification): void {
    if (!notification.id || notification.isUpdatingState) {
      return;
    }

    notification.isUpdatingState = true;

    this.subscriptions.push(
      this.notificationService.resolveAlertEvent(notification.id).subscribe({
        next: () => {
          notification.isUpdatingState = false;
          this.toastService.showSuccess('Alert resolved successfully', true);
          this.loadPage();
        },
        error: (error) => {
          notification.isUpdatingState = false;
          this.toastService.showError(error);
        }
      })
    );
  }

  openOnGraph(notification: AppNotification): void {
    if (!notification.endPointId) {
      this.toastService.showInfo('Node is not available for this notification', true);
      return;
    }

    const centerMs = this.toTimestampMs(notification.startedAt)
      ?? this.toTimestampMs(notification.shiftStartDate)
      ?? this.toTimestampMs(notification.sentDate)
      ?? this.toTimestampMs(notification.notificationSentDate)
      ?? Date.now();

    const { startMs, endMs } = buildAlertGraphWindowMs(centerMs);

    const queryParams: Record<string, string | number> = {
      dateFilterId: DateFilterTypeEnum.Custom,
      startDate: Math.floor(startMs / 1000),
      endDate: Math.floor(endMs / 1000),
      alertEventId: notification.id,
    };

    if (notification.organizationLevelId) {
      queryParams['selectedOrgId'] = notification.organizationLevelId;
    }

    if (notification.parameterId) {
      queryParams['selectedParameterId'] = notification.parameterId;
    }

    this.router.navigate(['/dashboard/node-detail', notification.endPointId], { queryParams });
  }

  private toTimestampMs(value: Date | string | number | null | undefined): number | null {
    return toTimestampMs(value, this.dateFormatService);
  }

}
